/**
 * Utility functions for handling images
 */

// Default avatar URL to use when profile images fail to load
export const DEFAULT_AVATAR_URL = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';

/**
 * Checks if a URL is a valid Supabase storage URL
 * @param url The URL to check
 * @returns True if the URL is a valid Supabase storage URL
 */
export const isValidSupabaseStorageUrl = (url: string | null): boolean => {
  if (!url) return false;
  
  try {
    const urlObj = new URL(url);
    // Check if the URL is from Supabase storage
    return urlObj.pathname.includes('/storage/v1/object/public/');
  } catch (error) {
    console.error('Invalid URL format:', url);
    return false;
  }
};

/**
 * Extracts the bucket name and path from a Supabase storage URL
 * @param url The Supabase storage URL
 * @returns An object with bucket and path, or null if invalid
 */
export const parseSupabaseStorageUrl = (url: string | null): { bucket: string; path: string } | null => {
  if (!url) return null;
  
  try {
    const urlObj = new URL(url);
    const pathParts = urlObj.pathname.split('/');
    
    // Find the 'public' part which comes after the bucket name
    const publicIndex = pathParts.findIndex(part => part === 'public');
    if (publicIndex === -1 || publicIndex === 0) return null;
    
    const bucket = pathParts[publicIndex - 1];
    const path = pathParts.slice(publicIndex + 1).join('/');
    
    return { bucket, path };
  } catch (error) {
    console.error('Error parsing storage URL:', url, error);
    return null;
  }
};

/**
 * Checks if an image URL is a default placeholder
 * @param url The URL to check
 * @returns True if the URL is a default placeholder
 */
export const isDefaultImage = (url: string | null): boolean => {
  if (!url) return false;
  return url.includes('upload.wikimedia.org') || url.includes('unsplash.com');
};

/**
 * Gets a safe image URL, falling back to the default if needed
 * @param url The original image URL
 * @param defaultUrl The default URL to use if the original is invalid
 * @returns A safe image URL
 */
export const getSafeImageUrl = (url: string | null, defaultUrl = DEFAULT_AVATAR_URL): string => {
  if (!url || url === '') return defaultUrl;
  
  // If it's a valid URL, return it
  try {
    new URL(url);
    return url;
  } catch (e) {
    return defaultUrl;
  }
};

/**
 * Checks if a URL is a Google Drive URL
 * @param url The URL to check
 * @returns True if the URL is a Google Drive URL
 */
export const isGoogleDriveUrl = (url: string): boolean => {
  if (!url) return false;
  return url.includes('drive.google.com/file/d/') || url.includes('drive.google.com/open?id=');
};

/**
 * Extracts the file ID from a Google Drive URL
 * @param url The Google Drive URL
 * @returns The file ID or null if not found
 */
export const extractGoogleDriveFileId = (url: string): string | null => {
  if (!url) return null;
  
  // Format: https://drive.google.com/file/d/{fileId}/view
  const fileIdMatch = url.match(/\/d\/([a-zA-Z0-9_-]+)(?:\/|$)/);
  if (fileIdMatch && fileIdMatch[1]) {
    return fileIdMatch[1];
  }
  
  // Format: https://drive.google.com/open?id={fileId}
  const idParamMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)(?:&|$)/);
  if (idParamMatch && idParamMatch[1]) {
    return idParamMatch[1];
  }
  
  return null;
};

/**
 * Converts a Google Drive URL to a direct download URL
 * @param url The Google Drive URL
 * @returns The direct download URL or the original URL if not a Google Drive URL
 */
export const convertGoogleDriveUrl = (url: string): string => {
  const fileId = extractGoogleDriveFileId(url);
  if (!fileId) {
    return url;
  }
  return `https://drive.google.com/uc?export=download&id=${fileId}`;
};